import { useState, useEffect } from 'react';

interface WeatherData {
  site_temperature: number | null;
  weatherCode: number | null;
  pm25: number | null;
  pm10: number | null;
  loading: boolean;
  error: string | null;
}

import { thingsboardService } from '../services/thingsboard';

export function useWeather(assetId: string | null) {
  const [data, setData] = useState<WeatherData>({
    site_temperature: null,
    weatherCode: null,
    pm25: null,
    pm10: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;
    let intervalId: ReturnType<typeof setInterval>;

    const fetchWeatherFromTB = async () => {
      if (!assetId) {
        if (isMounted) setData(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const telemetry = await thingsboardService.getLatestTelemetry(
          'ASSET',
          assetId,
          ['site_temperature', 'weatherCode', 'pm25', 'pm10']
        );

        if (isMounted) {
          setData({
            site_temperature: telemetry.site_temperature?.[0]?.value ?? null,
            weatherCode: telemetry.weatherCode?.[0]?.value ?? null,
            pm25: telemetry.pm25?.[0]?.value ?? null,
            pm10: telemetry.pm10?.[0]?.value ?? null,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        console.error("[useWeather] Failed to fetch telemetry from TB", err);
        if (isMounted) {
          setData(prev => ({ ...prev, loading: false, error: "Failed to fetch telemetry" }));
        }
      }
    };

    // Initial fetch
    fetchWeatherFromTB();

    // Updates every 10 minutes
    intervalId = setInterval(fetchWeatherFromTB, 10 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [assetId]);

  return data;
}
